// Rendered inline in the build-stream when the agent pauses on an
// `awaiting_input` event. Presents the agent's options as primary
// clickable buttons (when supplied) and always offers a subtle
// "write your own" disclosure — the agent's enumerated forks are
// usually right but the user should never feel boxed in.

import { useState } from "react";
import { Send } from "lucide-react";
import { answerBuild } from "../lib/api";
import { cn } from "../lib/cn";

interface Props {
  buildId: string;
  question: string;
  context?: string;
  options?: string[];
  /** Called after the answer POST returns 202 so the parent can
   *  optimistically switch state (e.g. hide the card, show a spinner). */
  onAnswered?: (answer: string) => void;
}

const ANSWER_MAX_CHARS = 2000;

export default function AskUserCard({
  buildId,
  question,
  context,
  options,
  onAnswered,
}: Props): JSX.Element {
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasOptions = Boolean(options && options.length > 0);
  // When the agent supplied options, free-form input is opt-in via a
  // subtle "write your own" toggle. When there are no options the
  // textarea is the only path, so it's open by default.
  const [customOpen, setCustomOpen] = useState<boolean>(!hasOptions);

  const submit = async (value: string): Promise<void> => {
    const trimmed = value.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await answerBuild(buildId, trimmed);
      onAnswered?.(trimmed);
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <section className="surface p-5 space-y-4 border-accent/40 bg-accent/5">
      <header className="space-y-1">
        <div className="eyebrow text-accent">The Oracle asks</div>
        <h3 className="font-display text-xl leading-tight text-fg">
          {question}
        </h3>
        {context && (
          <p className="text-sm text-fg-muted leading-relaxed">{context}</p>
        )}
      </header>

      {hasOptions && (
        <div className="flex flex-wrap gap-2">
          {options!.map((opt) => (
            <button
              key={opt}
              type="button"
              disabled={submitting}
              onClick={() => void submit(opt)}
              className="rounded-md border border-accent/50 bg-accent/10 hover:bg-accent/20 px-3 py-2 text-sm text-fg transition-colors disabled:opacity-40"
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {hasOptions && !customOpen && (
        <button
          type="button"
          onClick={() => setCustomOpen(true)}
          className="text-xs text-fg-muted hover:text-fg underline underline-offset-2 decoration-fg-subtle/40 hover:decoration-fg transition-colors"
        >
          or write your own
        </button>
      )}

      {customOpen && (
        <div className={cn("space-y-3", hasOptions && "pt-1")}>
          <textarea
            className="input min-h-[72px] font-sans text-sm leading-relaxed resize-y"
            placeholder={hasOptions ? "Something else…" : "Your answer…"}
            value={input}
            maxLength={ANSWER_MAX_CHARS}
            disabled={submitting}
            autoFocus={hasOptions}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                void submit(input);
              }
            }}
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-mono text-fg-subtle tabular-nums">
              {input.length}/{ANSWER_MAX_CHARS}
            </span>
            <button
              type="button"
              disabled={!input.trim() || submitting}
              onClick={() => void submit(input)}
              className="btn-primary h-9 text-sm"
            >
              <Send size={14} strokeWidth={2} />
              {submitting ? "Sending…" : "Answer"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-sm text-danger" role="alert">
          {error}
        </div>
      )}
    </section>
  );
}
